/**
 * Central place for turning API/network errors into safe, user-facing text.
 *
 * Never render `error.message`, `error.response.data`, or any raw
 * exception/JSON directly in the UI — always go through
 * `getErrorMessage()` / `handleApiError()` here so raw backend or
 * JavaScript internals never reach a user-facing screen, while the full
 * technical detail still reaches the console/logs for debugging.
 *
 * Backend responses are expected to eventually all use the structured shape
 *   { success: false, error: { code, message, userMessage, fields?, requestId? } }
 * but this also gracefully handles the legacy `{ detail: ... }` /
 * `{ message: ... }` shapes that much of the codebase still returns, so it's
 * safe to adopt incrementally.
 */

export const ERROR_CODES = {
  VALIDATION: 'VALIDATION_ERROR',
  AUTHENTICATION: 'AUTHENTICATION_ERROR',
  AUTHORIZATION: 'AUTHORIZATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  EXPIRED: 'EXPIRED',
  NETWORK: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT_ERROR',
  SERVER: 'SERVER_ERROR',
  FILE_UPLOAD: 'FILE_UPLOAD_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR',
};

const STATUS_FALLBACKS = {
  400: "We couldn't process your request. Please check your input and try again.",
  401: 'Your session could not be verified. Please sign in again.',
  403: "You don't have permission to perform this action. Please contact your administrator if you believe this is incorrect.",
  404: 'This item could not be found. It may have been removed or is no longer available.',
  409: "This action can't be completed because it conflicts with existing data. Please refresh the page and try again.",
  410: 'This is no longer available — it may have expired or already been completed.',
  413: 'This file is too large to upload. Please choose a smaller file.',
  422: 'Some required information is missing or invalid. Please check the highlighted fields and try again.',
  429: "You're doing that too quickly. Please wait a moment and try again.",
};

const STATUS_CODES = {
  400: ERROR_CODES.VALIDATION,
  401: ERROR_CODES.AUTHENTICATION,
  403: ERROR_CODES.AUTHORIZATION,
  404: ERROR_CODES.NOT_FOUND,
  409: ERROR_CODES.CONFLICT,
  410: ERROR_CODES.EXPIRED,
  422: ERROR_CODES.VALIDATION,
};

export const SERVER_ERROR_MESSAGE =
  "Something went wrong on our side. Your existing data has not been intentionally changed. Please try again in a moment.";
export const NETWORK_ERROR_MESSAGE =
  "We couldn't connect to the server. Please check your internet connection and try again.";
export const TIMEOUT_ERROR_MESSAGE = 'That took longer than expected and timed out. Please try again.';
export const UNKNOWN_ERROR_MESSAGE =
  "We couldn't complete that action. Please try again. If the problem continues, contact your administrator.";

/** Heuristic: does this string look like a leaked technical/exception message
 * rather than something written for an end user? */
function looksTechnical(text) {
  if (!text || typeof text !== 'string') return true;
  const trimmed = text.trim();
  if (!trimmed || /^(undefined|null|nan|\[object object\])$/i.test(trimmed)) return true;
  const lowered = trimmed.toLowerCase();
  const markers = [
    'traceback', 'stack trace', 'nonetype', 'undefined is not', 'null is not',
    'cannot read propert', 'sqlalchemy', 'psycopg', 'pymysql', 'exception',
    'request failed with status code', 'network error', 'axios', 'fetch failed',
    ' at 0x', 'object has no attribute', '<!doctype', '<html', 'internal server error',
    'validationerror', 'keyerror', 'typeerror', 'attributeerror',
  ];
  return markers.some((m) => lowered.includes(m));
}

function normalizeLegacyDetail(detail) {
  if (Array.isArray(detail)) {
    // FastAPI/pydantic validation error list: [{ loc, msg, type }, ...]
    return detail.map((d) => d?.msg || d?.message).filter(Boolean).join(' ');
  }
  if (detail && typeof detail === 'object') {
    return detail.message || detail.detail || null;
  }
  return detail;
}

/**
 * Parse a caught error (typically an axios error) into a safe, structured
 * shape the UI can use: { code, userMessage, status, fields, retryable, requestId }.
 */
export function parseApiError(error, fallbackMessage) {
  if (!error) {
    return {
      code: ERROR_CODES.UNKNOWN,
      userMessage: fallbackMessage || UNKNOWN_ERROR_MESSAGE,
      status: null,
      fields: null,
      retryable: true,
      requestId: null,
    };
  }

  if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
    return {
      code: ERROR_CODES.TIMEOUT,
      userMessage: TIMEOUT_ERROR_MESSAGE,
      status: null,
      fields: null,
      retryable: true,
      requestId: null,
    };
  }

  if (!error.response) {
    // Request was made but no response came back (offline, DNS, CORS, server down).
    return {
      code: ERROR_CODES.NETWORK,
      userMessage: NETWORK_ERROR_MESSAGE,
      status: null,
      fields: null,
      retryable: true,
      requestId: null,
    };
  }

  const { status, data, headers } = error.response;
  const requestId = data?.error?.requestId || headers?.['x-request-id'] || null;

  // New structured shape.
  const structured = data?.error;
  if (structured?.userMessage) {
    return {
      code: structured.code || STATUS_CODES[status] || ERROR_CODES.UNKNOWN,
      userMessage: structured.userMessage,
      status,
      fields: structured.fields || null,
      retryable: status >= 500 || status === 429,
      requestId,
    };
  }

  // Legacy shape: { detail: "..." | [...] | {...} } or { message: "..." }.
  const candidate = normalizeLegacyDetail(data?.detail) || data?.message;
  const fallback =
    STATUS_FALLBACKS[status] || fallbackMessage || (status >= 500 ? SERVER_ERROR_MESSAGE : UNKNOWN_ERROR_MESSAGE);
  const userMessage = candidate && !looksTechnical(candidate) ? candidate : fallback;

  // Legacy validation shape sometimes includes a field-level `errors` array
  // ({ detail: "Validation error", errors: [{ loc: [...], msg }] }).
  let fields = null;
  if (Array.isArray(data?.errors)) {
    fields = {};
    data.errors.forEach((e) => {
      const loc = Array.isArray(e?.loc) ? e.loc.filter((p) => p !== 'body' && p !== 'query') : [];
      const key = loc.join('.') || 'form';
      fields[key] = e?.msg || 'This field is invalid.';
    });
  }

  return {
    code: STATUS_CODES[status] || (status >= 500 ? ERROR_CODES.SERVER : ERROR_CODES.UNKNOWN),
    userMessage,
    status,
    fields,
    retryable: status >= 500 || status === 429,
    requestId,
  };
}

/** Shortcut for the common case: just get the string to show the user. */
export function getErrorMessage(error, fallbackMessage) {
  return parseApiError(error, fallbackMessage).userMessage;
}

/** { field: message } map for highlighting specific form fields, or null. */
export function getFieldErrors(error) {
  return parseApiError(error).fields;
}

/** Logs full technical detail for developers — never render this in the UI. */
export function logErrorForDeveloper(context, error, extra = {}) {
  // eslint-disable-next-line no-console
  console.error(`[${context}]`, {
    message: error?.message,
    status: error?.response?.status,
    url: error?.config?.url,
    method: error?.config?.method,
    responseData: error?.response?.data,
    requestId: error?.response?.headers?.['x-request-id'] || error?.response?.data?.error?.requestId,
    ...extra,
  });
}

/**
 * Convenience wrapper: logs the technical error for developers and returns
 * the parsed, user-safe error info in one call.
 *   const { userMessage } = handleApiError(err, { context: 'SaveQuestion' });
 *   toast.error(userMessage);
 */
export function handleApiError(error, { context = 'API', fallbackMessage, extra } = {}) {
  logErrorForDeveloper(context, error, extra);
  return parseApiError(error, fallbackMessage);
}
